import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { PaginatedSelectedFilterProps } from '@atlassian/filter-controller';
import type {
  AsyncLoadOptions,
  SingleOption,
  SelectOption,
} from '@atlassian/paginated-filter';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { getStatusMessage } from '@app/app';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';
import { STATUS_IDS } from '../constants';
import type { UpdateStatus } from '../types';

export const messages = defineMessages({
  status: {
    id: 'appUpdates.filters.status.label',
    description: 'Label for the status filter',
    defaultMessage: 'Status',
  },
});

const useStatusOptions = (): UpdateStatus[] => {
  const { formatMessage } = useIntl();

  return STATUS_IDS.map((statusId) => ({
    id: statusId,
    name: formatMessage(getStatusMessage(statusId)),
  }));
};

export const useStatusesFromOptions = () => {
  const statusOptions = useStatusOptions();

  return useCallback(
    (options: readonly SingleOption[]): UpdateStatus[] | null => {
      const selectedStatuses = options
        .map((option) =>
          statusOptions.find((candidate) => candidate.id === option.value),
        )
        .filter((status): status is UpdateStatus => status !== undefined);

      return selectedStatuses.length > 0 ? selectedStatuses : null;
    },
    [statusOptions],
  );
};

export const useStatusFilterConfig = (): PaginatedSelectedFilterProps => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { sendUIEvent } = useAnalyticsEvent();
  const statusOptions = useStatusOptions();
  const getStatusesFromOptions = useStatusesFromOptions();

  const statusSelectOptions: SingleOption[] = statusOptions
    .map(
      (status): SingleOption => ({
        label: status.name,
        value: status.id,
        optionType: 'option',
      }),
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  const getSelectedStatuses = (): SingleOption[] => {
    const selectedStatuses = state.updates.draftFilters.selectedStatuses;

    if (!selectedStatuses || selectedStatuses.length === 0) {
      return [];
    }

    return selectedStatuses
      .map((selectedStatus) =>
        statusSelectOptions.find(
          (option) => option.value === selectedStatus.id,
        ),
      )
      .filter((option): option is SingleOption => option !== undefined);
  };

  const onStatusFilterChange = (options: readonly SingleOption[]) => {
    const selectedStatusValues = getStatusesFromOptions(options) ?? [];

    dispatch({
      type: 'FILTER_STATUS_SELECT',
      payload: {
        selectedStatuses:
          selectedStatusValues.length > 0 ? selectedStatusValues : null,
      },
    });

    // Send analytics event
    const updatesStatusTypes =
      selectedStatusValues.length > 0
        ? selectedStatusValues.map((s) => s.id).join(',')
        : undefined;
    sendUIEvent(
      appUpdatesUIEvents.ui.appUpdatesStatusFilterClicked(
        { source: 'appUpdatesListScreen' },
        updatesStatusTypes,
      ).data,
    );
  };

  const onLoadOptions: AsyncLoadOptions = (
    search,
    _enableFullList,
    _showFullList,
    _showMore,
    _lastOption,
    callback,
  ) => {
    let options: SelectOption[] = statusSelectOptions.map((option) => ({
      label: option.label,
      value: option.value,
      optionType: 'option',
    }));

    if (search) {
      const searchText = search.toLowerCase();
      options = options.filter((option) =>
        option.label?.toLowerCase().includes(searchText),
      );
    }

    callback({
      options,
      totalCount: options.length,
    });
  };

  return {
    testId: 'app-updates-filter-status-testid',
    fieldLabel: formatMessage(messages.status),
    fieldId: 'app-updates-table-status-filter',
    onLoadOptions,
    value: getSelectedStatuses(),
    onChange: onStatusFilterChange,
    onClearValues: () => onStatusFilterChange([]),
  };
};
